// Procedural texture provider. This is the swappable art source: today it
// synthesizes tech-panel wall/floor/ceiling textures deterministically at
// runtime; later the same `TextureProvider` interface will be backed by baked
// PNG atlases loaded from /preview-assets-style static assets. Gameplay code
// never references a concrete source, only the interface.

import { mulberry32, packRGBA } from "./math";
import type { Texture, TextureProvider } from "./types";

const SIZE = 64;

// Texture ids (kept stable; the level grid references wall ids 1..3).
export const TEX = {
  WALL_PANEL: 1,
  WALL_TRIM: 2,
  WALL_HAZARD: 3,
  DOOR: 4,
  FLOOR: 10,
  CEIL: 11,
} as const;

function make(): Uint32Array {
  return new Uint32Array(SIZE * SIZE);
}

function put(data: Uint32Array, x: number, y: number, px: number): void {
  if (x < 0 || y < 0 || x >= SIZE || y >= SIZE) return;
  data[y * SIZE + x] = px;
}

/** Metal tech panel: base fill, beveled border (light top-left, dark bottom-right),
 *  a horizontal seam, corner rivets, an accent emissive strip, and fine noise. */
function buildPanel(
  seed: number,
  base: [number, number, number],
  accent: [number, number, number],
): Uint32Array {
  const data = make();
  const rng = mulberry32(seed);
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const n = (rng() - 0.5) * 18;
      let r = base[0] + n;
      let g = base[1] + n;
      let b = base[2] + n;
      // Bevel: brighter near top/left, darker near bottom/right edges.
      const edge = Math.min(x, y, SIZE - 1 - x, SIZE - 1 - y);
      if (edge < 3) {
        const lit = x + y < SIZE ? 1.5 : 0.55;
        r *= lit;
        g *= lit;
        b *= lit;
      }
      // Horizontal seam across the middle.
      if (y === 31 || y === 32) {
        r *= 0.5;
        g *= 0.5;
        b *= 0.5;
      }
      put(data, x, y, packRGBA(clampByte(r), clampByte(g), clampByte(b)));
    }
  }
  // Corner rivets.
  const rivets: ReadonlyArray<readonly [number, number]> = [
    [6, 6],
    [SIZE - 7, 6],
    [6, SIZE - 7],
    [SIZE - 7, SIZE - 7],
  ];
  for (const [rx, ry] of rivets) {
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        if (dx * dx + dy * dy > 4) continue;
        const lit = dx + dy < 0 ? 1.7 : 0.8;
        put(
          data,
          rx + dx,
          ry + dy,
          packRGBA(clampByte(base[0] * lit + 30), clampByte(base[1] * lit + 30), clampByte(base[2] * lit + 34)),
        );
      }
    }
  }
  // Accent emissive strip (brand blue/orange), left of center.
  for (let y = 14; y < SIZE - 14; y++) {
    put(data, 20, y, packRGBA(accent[0], accent[1], accent[2]));
    put(data, 21, y, packRGBA(accent[0], accent[1], accent[2]));
  }
  return data;
}

function buildFloor(): Uint32Array {
  const data = make();
  const rng = mulberry32(0x1077);
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const n = (rng() - 0.5) * 12;
      let r = 16 + n;
      let g = 22 + n;
      let b = 30 + n;
      // Faint blue grid lines every 16px.
      if (x % 16 === 0 || y % 16 === 0) {
        r += 6;
        g += 30;
        b += 44;
      }
      put(data, x, y, packRGBA(clampByte(r), clampByte(g), clampByte(b)));
    }
  }
  return data;
}

function buildCeil(): Uint32Array {
  const data = make();
  const rng = mulberry32(0xce11);
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const n = (rng() - 0.5) * 8;
      let r = 9 + n;
      let g = 11 + n;
      let b = 16 + n;
      // Occasional recessed light panel.
      if (x > 26 && x < 38 && y > 26 && y < 38) {
        r += 40;
        g += 70;
        b += 90;
      }
      put(data, x, y, packRGBA(clampByte(r), clampByte(g), clampByte(b)));
    }
  }
  return data;
}

function clampByte(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v | 0;
}

export function createProceduralTextures(): TextureProvider {
  const cache = new Map<number, Texture>();
  const builders: Record<number, () => Uint32Array> = {
    [TEX.WALL_PANEL]: () => buildPanel(0x5151, [28, 40, 58], [64, 210, 255]),
    [TEX.WALL_TRIM]: () => buildPanel(0x7a3a, [34, 34, 42], [255, 138, 34]),
    [TEX.WALL_HAZARD]: () => buildPanel(0x9b1b, [42, 20, 24], [255, 70, 70]),
    [TEX.DOOR]: () => buildPanel(0x0d00, [20, 46, 40], [66, 226, 195]),
    [TEX.FLOOR]: buildFloor,
    [TEX.CEIL]: buildCeil,
  };
  return {
    get(id: number): Texture {
      let tex = cache.get(id);
      if (!tex) {
        const build = builders[id] ?? builders[TEX.WALL_PANEL]!;
        tex = { size: SIZE, data: build() };
        cache.set(id, tex);
      }
      return tex;
    },
  };
}
